import React, { useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import { Typography, Icon, Paper } from '@material-ui/core';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import TableOmni from 'allGlobalComponents/TableOmni';
import FuseLoading from '@fuse/core/FuseLoading';
import Box from '@mui/material/Box';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import moment from 'moment';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import CopyValue from 'allGlobalComponents/CopyValue';
import { createMuiTheme, makeStyles } from '@material-ui/core/styles';

// Custom themes for input styling (copied from Messages.js)
const SearchStyle = createMuiTheme({
	overrides: {
		MuiInput: {
			root: {
				paddingTop: 0,
				fontSize: '12px',
				paddingBottom: 0,
				margin: 0,
				border: 0,
				borderRadius: 0,
				height: '30px'
			}
		}
	}
});

const useStyles = makeStyles(theme => ({
	btn: { backgroundColor: theme.palette.secondary.main },
	datePickerParent: {
		'& .DateRangePickerInput_arrow': { color: theme.palette.secondary.value },
		'& .DateInput_input__small_2': { color: theme.palette.secondary.main },
		'& .DateInput_input::placeholder': { color: theme.palette.secondary.value },
		'& > *': { padding: '2% 0% 3% 0%', color: theme.palette.secondary.value}
	}
}));

function OTPLogsReport() {
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [totalItems, setTotalItems] = useState(0);
	const [currentParams, setCurrentParams] = useState({ limit: 10, page: 0 });

	// Search and date state
	const [searchEmail, setSearchEmail] = useState('');
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const classes = useStyles();
	const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);

	// Set default date range: start date July 25, 2025, end date today, but picker fields empty by default
	useEffect(() => {
		setStartDate(null);
		setEndDate(null);
	}, []);

	const defaultStartDate = moment();
	const defaultEndDate = moment();

	// Fetch logs when params or date changes
	useEffect(() => {
		// Use default range if both are null
		const fetchStart = startDate === null ? defaultStartDate : startDate;
		const fetchEnd = endDate === null ? defaultEndDate : endDate;
		if (fetchStart && fetchEnd) {
			fetchOTPLogs(fetchStart, fetchEnd);
		}
		// eslint-disable-next-line
	}, [currentParams, startDate, endDate]);

	const fetchOTPLogs = (fetchStart, fetchEnd) => {
		setIsLoading(true);

		const params = {
			clientId: '1',
			// provider_id: '1',
			...currentParams,
			startDate: moment(fetchStart).startOf('day').toISOString(),
			endDate: moment(fetchEnd).endOf('day').toISOString()
		};

		if (searchEmail) {
			params.email = searchEmail;
		}

		CoreHttpHandler.request(
			'otp',
			'otplogs',
			params,
			(response) => {
				const res = response.data.data.logs.data || [];
				const total = response.data.data.logs.totalItems || 0;
				setData(res);
				setTotalItems(total);
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				setIsLoading(false);
			}
		);
	};

	const handleSearch = () => {
		setCurrentParams((prev) => ({
			...prev,
			page: 0
		}));
		// fetchOTPLogs(); // No need, already handled by useEffect
	};



	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const handleDateChange = ({ startDate, endDate }) => {
		setStartDate(startDate);
		setEndDate(endDate);
		setCurrentParams((prev) => ({
			...prev,
			page: 0
		}));
	};

	const headCells = [
		{ id: 'id', numeric: true, disablePadding: false, label: 'Id' },
		{ id: 'username', label: 'UserName' },
		{
			id: 'email',
			label: 'Email',
			customRow: row => (
				<CopyValue
					val={row.email}
					inner={row.email}
					tooltipTxt="Email"
					styles={{ textDecoration: 'none' }}
				/>
			)
		},
		{ id: 'number', label: 'Phone Number' },
		{ id: 'otp_code', label: 'OTP Code' },
		{ id: 'dt', label: 'Date & Time' },
		{ id: 'provider_name', label: 'Provider' },
		{ id: 'provider_email', label: 'Provider Email' },
	];

	return (
		<FusePageSimple
			classes={{
				contentWrapper: 'p-0 pb-80 sm:pb-80 h-full z-999',
				content: 'flex flex-col pr-10',
				header: 'md:h-80 sm:h-134 sm:min-h-136 xs:h-full',
				wrapper: 'min-h-0 z-0'
			}}
			header={
				<div className="flex flex-1 w-full items-center px-20">
					<div className="flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<SmartphoneIcon fontSize="large" />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="hidden sm:flex mx-0 sm:mx-12" variant="h6">
								<span style={{ fontSize: '15px' }}>Otp Logs</span>
							</Typography>
						</FuseAnimate>
					</div>
					<div className="flex flex-1 items-center justify-center px-8 sm:px-12">
						<Paper
							className="flex items-center w-full flex-wrap max-w-xl px-4 py-4"
							elevation={1}
							style={{ background: 'none', boxShadow: 'none', color: 'white' }}
						>
							<Box className={classes.datePickerParent} style={{ marginRight: '16px', position: 'relative', zIndex: 9999 }}>
								<DateRangePickerOmni
									startDate={startDate}
									endDate={endDate}
									onDateChange={handleDateChange}
									defaultNull={true}
								/>
							</Box>
							<ThemeProvider theme={mainTheme}>
								<FuseAnimate animation="transition.slideLeftIn" delay={300}>
									<Paper className="flex items-center w-full max-w-sm px-8 py-4" elevation={1} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
										<Icon color="action" fontSize="small">search</Icon>
										<FormFieldOmni
											style={{
												border: 'none',
												color: 'black',
												width: '100%',
												padding: '0 8px'
											}}
											placeholder="Search by Email"
											value={searchEmail}
											onChange={e => setSearchEmail(e.target.value)}
											InputProps={{
												disableUnderline: true,
												style: { fontSize: '12px', height: '30px', color:'theme.palette.secondary.value'}
											}}
											onKeyDown={handleKeyDown}
										/>
										<ButtonOmni
											onClick={handleSearch}
											id="otp-search-button"
											label="Search"
											style={{ marginLeft: '8px', fontSize: '11px' }}
											size="small"
											variant="outlined"
										/>
										<ButtonOmni
											onClick={() => {
												setSearchEmail("");
												setCurrentParams((prev) => ({ ...prev, page: 0 }));
											}}
											id="otp-clear-button"
											label="Clear"
											style={{ marginLeft: '8px', fontSize: '11px' }}
											size="small"
											variant="outlined"
										/>
									</Paper>
								</FuseAnimate>
							</ThemeProvider>
						</Paper>
					</div>
				</div>
			}
			content={
				<>
					{isLoading ? (
						<FuseLoading />
					) : (
						<Box m={2} px={2}>
							<TableOmni
								rows={data}
								totalItems={totalItems}
								headCells={headCells}
								perPageRows={currentParams.limit}
								curPage={currentParams.page}
								setCurrentParams={setCurrentParams}
							/>
						</Box>
					)}
				</>
			}
		/>
	);
}

export default OTPLogsReport;